import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/** One preset brand color offered in the brand step. */
export interface WebsiteBrandColorSwatch {
  /** Six-digit HEX value written to the theme. */
  value: string;
  /** Translation key of the accessible color name. */
  labelKey: TranslationKey;
}

/** Preset brand colors that read well in every design. */
export const WEBSITE_BRAND_COLOR_SWATCHES: readonly WebsiteBrandColorSwatch[] =
  [
    {
      value: '#1E6B52',
      labelKey: 'websites.settings.brand.color.swatch.green',
    },
    { value: '#1F4E8C', labelKey: 'websites.settings.brand.color.swatch.blue' },
    {
      value: '#A8321F',
      labelKey: 'websites.settings.brand.color.swatch.terracotta',
    },
    {
      value: '#E3A92B',
      labelKey: 'websites.settings.brand.color.swatch.yellow',
    },
    {
      value: '#7C3AED',
      labelKey: 'websites.settings.brand.color.swatch.violet',
    },
    {
      value: '#111827',
      labelKey: 'websites.settings.brand.color.swatch.black',
    },
  ];

/** Dark text color used on light brand colors. */
export const WEBSITE_BRAND_DARK_TEXT_COLOR = '#111827';

/** Minimum WCAG contrast ratio for normal-size text. */
export const WEBSITE_BRAND_MIN_TEXT_CONTRAST = 4.5;

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** Text tone that reads best on a brand color. */
export type WebsiteBrandTextTone = 'light' | 'dark';

/**
 * Checks for a complete six-digit HEX color such as `#1E6B52`.
 *
 * @param value - Color typed by the user.
 * @returns Whether the value is a valid six-digit HEX color.
 */
export const isWebsiteHexColor = (value: string | undefined): boolean =>
  Boolean(value && HEX_COLOR_PATTERN.test(value));

/**
 * Checks whether two HEX colors are the same, ignoring letter case.
 *
 * @param left - First HEX color.
 * @param right - Second HEX color.
 * @returns Whether both values name the same color.
 */
export const isSameWebsiteHexColor = (
  left: string | undefined,
  right: string | undefined
): boolean =>
  Boolean(left && right && left.toLowerCase() === right.toLowerCase());

const toLinearChannel = (channel: number): number => {
  const normalized = channel / 255;

  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

/**
 * Computes the WCAG relative luminance of a HEX color.
 *
 * @param hexColor - Six-digit HEX color.
 * @returns Relative luminance from 0 (black) to 1 (white).
 * @throws {Error} When the value is not a six-digit HEX color.
 */
export const getWebsiteColorLuminance = (hexColor: string): number => {
  if (!isWebsiteHexColor(hexColor)) {
    throw new Error(`Expected a six-digit HEX color, received ${hexColor}.`);
  }

  const red = toLinearChannel(Number.parseInt(hexColor.slice(1, 3), 16));
  const green = toLinearChannel(Number.parseInt(hexColor.slice(3, 5), 16));
  const blue = toLinearChannel(Number.parseInt(hexColor.slice(5, 7), 16));

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Computes the WCAG contrast ratio between two HEX colors.
 *
 * @param foreground - Six-digit HEX text color.
 * @param background - Six-digit HEX surface color.
 * @returns Contrast ratio from 1 to 21.
 * @throws {Error} When either value is not a six-digit HEX color.
 */
export const getWebsiteColorContrast = (
  foreground: string,
  background: string
): number => {
  const foregroundLuminance = getWebsiteColorLuminance(foreground);
  const backgroundLuminance = getWebsiteColorLuminance(background);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Picks white text when it reaches the WCAG minimum on the brand color, and
 * dark text otherwise.
 *
 * @param brandColor - Six-digit HEX brand color.
 * @returns The text tone for buttons filled with the brand color.
 * @throws {Error} When the value is not a six-digit HEX color.
 */
export const resolveWebsiteBrandTextTone = (
  brandColor: string
): WebsiteBrandTextTone =>
  getWebsiteColorContrast('#FFFFFF', brandColor) >=
  WEBSITE_BRAND_MIN_TEXT_CONTRAST
    ? 'light'
    : 'dark';
